import { ChartTooltipContent } from "@/components/Chart";
import { BaseInsightLineChart } from "@/components/insights/BaseInsightLineChart";
import { PRIMARY_AXIS_COLOR, SECONDARY_AXIS_COLOR } from "@/components/insights/insights-constants";
import { formatShortDate } from "@/format/time";
import { useActiveUsersTimeSeries } from "@/queries/insights-queries";
import type { TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";
import * as RechartsPrimitive from "recharts";

type PointPayload = {
    timestamp: Timestamp;
    users: number;
    returningUsers: number;
    date: Date;
};

type Props = {
    startTimestamp: Timestamp;
    endTimestamp: Timestamp;
    selectedRange: TimeRange;
    projectId?: string;
};
export const ActiveUsersLineChart: FC<Props> = ({ startTimestamp, endTimestamp, selectedRange, projectId }) => {
    const { data, isLoading } = useActiveUsersTimeSeries(startTimestamp, endTimestamp, selectedRange, projectId);

    // Format data for the chart
    const chartData = useMemo(() => {
        if (!data?.weeklyActiveUsers) return [];

        return data.weeklyActiveUsers.map((point, index) => {
            const dateObj = point.time ? point.time.toDate() : new Date();
            const returningUsers = data.weeklyActiveReturningUsers?.[index]
                ? Number(data.weeklyActiveReturningUsers[index].value || 0)
                : 0;

            return {
                date: dateObj,
                timestamp: dateObj.getTime(),
                users: point.value ? Number(point.value) : 0,
                returningUsers: returningUsers,
            };
        });
    }, [data]);

    return (
        <div className="w-full">
            <BaseInsightLineChart
                isLoading={isLoading}
                chartData={chartData}
                chartConfig={{
                    users: {
                        label: "Active Users",
                        color: PRIMARY_AXIS_COLOR,
                    },
                    returningUsers: {
                        label: "Active users w/ 2+ days",
                        color: SECONDARY_AXIS_COLOR,
                    },
                }}
                tooltipContent={
                    <ChartTooltipContent
                        labelFormatter={(_: string | number | Date, payload: unknown[]) => {
                            if (!payload || payload.length === 0) return "";
                            const item = payload[0] as { payload: PointPayload };
                            if (!(item.payload.date instanceof Date)) return "";
                            return formatShortDate(item.payload.date, true);
                        }}
                    />
                }
            >
                <RechartsPrimitive.Line
                    type="monotone"
                    dataKey="users"
                    name="users"
                    stroke="var(--color-users)"
                    strokeWidth={2}
                    dot={{ r: 0 }}
                    activeDot={{
                        r: 6,
                        stroke: "var(--color-users)",
                        strokeWidth: 2,
                        fill: "rgb(var(--ona-orange-100))",
                    }}
                    xAxisId="0"
                />
                <RechartsPrimitive.Line
                    type="monotone"
                    dataKey="returningUsers"
                    name="returningUsers"
                    stroke="var(--color-returningUsers)"
                    strokeWidth={2}
                    dot={{ r: 0 }}
                    activeDot={{
                        r: 6,
                        stroke: "var(--color-returningUsers)",
                        strokeWidth: 2,
                        fill: "rgb(var(--ona-blue-100))",
                    }}
                    xAxisId="0"
                />
            </BaseInsightLineChart>
        </div>
    );
};
